import { type ReactNode, useCallback, useEffect, useMemo } from 'react'

import { usePreferences } from '../hooks/usePreferences'
import { ThemeContext } from './ThemeContext'

export type Theme = 'light' | 'dark'

/**
 * Dark for anything that is not the one other theme there is.
 *
 * The stored blob comes from a browser this app does not control: an older
 * build's shape, a hand-edited value, a theme a later build dropped. Every
 * path that reads one normalises through here, including the pre-paint script,
 * which cannot import it and states the same rule in its own words.
 */
export const asTheme = (value: unknown): Theme =>
  value === 'light' ? 'light' : 'dark'

export interface ThemeProviderProps {
  children: ReactNode
  /**
   * Where this app's preferences live — the same key its pre-paint script
   * reads, or the two disagree for one frame on every load.
   */
  storageKey: string
  /**
   * What to open on when nothing is stored. A thunk is resolved once, so an
   * app deriving it from `prefers-color-scheme` does not consult the browser
   * on every render.
   */
  defaultTheme: Theme | (() => Theme)
}

/**
 * Drives {@link ThemeContext} from a stored preference, and puts the result on
 * `<html data-theme>`, which the Meridian palette keys off.
 *
 * Shared rather than written per app because both apps want the same three
 * things — read the stored theme, mirror it onto the document, offer a toggle
 * — and differ only in where they keep it and what they open on. Those are the
 * two parameters.
 *
 * @example
 * // The customer app follows the system, and keeps the key its users already
 * // have.
 * <ThemeProvider storageKey="userPreferences" defaultTheme={getSystemTheme}>
 *
 * @example
 * // The staff console is dark whatever the system says, so an operator can
 * // tell it from the customer app at a glance.
 * <ThemeProvider storageKey="admin.userPreferences" defaultTheme="dark">
 */
export const ThemeProvider = ({
  children,
  storageKey,
  defaultTheme,
}: ThemeProviderProps) => {
  const [preferences, setPreferences] = usePreferences<{ theme: Theme }>(
    storageKey,
    () => ({
      theme: typeof defaultTheme === 'function' ? defaultTheme() : defaultTheme,
    }),
  )
  const theme = asTheme(preferences.theme)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  const toggleTheme = useCallback(() => {
    // A functional update, and a spread: the key can hold more than a theme
    // (the customer app keeps its table settings in the same blob), and a
    // value captured from an earlier render would write those back stale.
    setPreferences((previous) => ({
      ...previous,
      theme: asTheme(previous.theme) === 'dark' ? 'light' : 'dark',
    }))
  }, [setPreferences])

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
